import { ReactNode, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, AlertCircle, X, RotateCcw } from 'lucide-react';
import { WizardProgress } from './WizardProgress';
import { BrandLockup } from '@/components/app/BrandLockup';

interface WizardStepLayoutProps {
  currentStep: number;
  totalSteps: number;
  title: string;
  subtitle: string;
  children: ReactNode;
  onNext: () => void;
  onBack?: () => void;
  nextLabel?: string;
  /** @deprecated Use validationErrors instead — kept for backwards compat */
  nextDisabled?: boolean;
  showBack?: boolean;
  /** Validation errors to show in modal when Continue is clicked. Empty array = valid. */
  validationErrors?: string[];
  /** Called before navigation to trigger field highlights in the step */
  onSubmitAttempt?: () => void;
  /**
   * If set (non-empty string), Continue shows a dismissible reminder instead of
   * navigating straight away — for input the user has typed/selected in this step
   * but not yet committed with its own Add/Save/Upload button. Unlike
   * validationErrors, this never blocks: the user can proceed anyway.
   */
  unsavedDraftWarning?: string;
  /** If provided, shows a "Back to Review" shortcut button */
  onReturnToReview?: () => void;
  /** Optional sub-step label e.g. "School Type (1 of 5)" — shown below the progress bar */
  subStepInfo?: string;
}

const stepLabels = ['Verify', 'Contact', 'Configure', 'Staff', 'Policies'];

/**
 * Publishes this footer's height as `--cmc-wizard-footer-height`, the same
 * convention the tutorial bar and the readiness drawer already use.
 *
 * Floating UI anchors itself above whatever bottom bar is on screen by summing
 * those vars. The wizard footer never published one, so the demo inbox bell sat
 * at its default offset and covered the Continue / Next buttons on a phone
 * (screenshots 01 and 03, 2026-08-20 — the labels read "Continu…" and "Nex…").
 * Fixing it here rather than special-casing the bell means anything else that
 * floats gets the same clearance for free.
 */
function usePublishFooterHeight() {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = document.documentElement;
    const publish = () =>
      root.style.setProperty('--cmc-wizard-footer-height', `${Math.round(el.getBoundingClientRect().height)}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--cmc-wizard-footer-height');
    };
  }, []);
  return ref;
}

export function WizardStepLayout({
  currentStep,
  totalSteps,
  title,
  subtitle,
  children,
  onNext,
  onBack,
  nextLabel = 'Continue',
  nextDisabled = false,
  showBack = true,
  validationErrors,
  onSubmitAttempt,
  onReturnToReview,
  subStepInfo,
  unsavedDraftWarning,
}: WizardStepLayoutProps) {
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const footerRef = usePublishFooterHeight();

  // Scroll to top whenever a new step mounts
  useEffect(() => {
    // Instant, not smooth: the step is fading in at the same time, and a
    // smooth scroll stacked more perceived delay onto an already slow change.
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  const handleContinue = () => {
    onSubmitAttempt?.();
    if (validationErrors && validationErrors.length > 0) {
      setShowErrorModal(true);
      return;
    }
    // Fallback: legacy nextDisabled behaviour
    if (nextDisabled) return;
    if (unsavedDraftWarning) {
      setShowUnsavedModal(true);
      return;
    }
    onNext();
  };

  return (
    <div className="min-h-screen bg-background paper-dots flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 md:px-8 md:py-6">
          <div className="flex items-center justify-between mb-4">
            {showBack && onBack ? (
              <button
                onClick={onBack}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm font-medium hidden sm:inline">Back</span>
              </button>
            ) : (
              <div />
            )}
            <BrandLockup size={13} />
          </div>
          <WizardProgress
            currentStep={currentStep}
            totalSteps={totalSteps}
            labels={stepLabels}
            subStepInfo={subStepInfo}
          />
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-3xl mx-auto px-4 py-8 md:px-8 md:py-12">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              {title}
            </h1>
            <p className="text-muted-foreground mb-8">
              {subtitle}
            </p>

            {children}
          </motion.div>
        </div>
      </main>

      {/* Footer with navigation */}
      <footer ref={footerRef} className="border-t border-border bg-card/50 backdrop-blur-sm sticky bottom-0">
        <div className="max-w-3xl mx-auto px-4 py-4 md:px-8 md:py-6">
          <div className="flex items-center justify-between gap-4">
            {showBack && onBack ? (
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-muted text-foreground hover:bg-muted/80 transition-colors text-sm font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Previous
              </button>
            ) : (
              <div className="text-sm text-muted-foreground hidden sm:block">
                Your progress is saved automatically
              </div>
            )}
            <div className="flex items-center gap-2">
              {onReturnToReview && (
                <button
                  onClick={onReturnToReview}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border bg-card text-foreground hover:bg-muted/60 transition-colors text-sm font-medium"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Back to Review</span>
                  <span className="sm:hidden">Review</span>
                </button>
              )}
              <button
                onClick={handleContinue}
                className="btn-accent"
              >
                {nextLabel}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Validation error modal */}
      <AnimatePresence>
        {showErrorModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowErrorModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ duration: 0.2 }}
              className="bg-card rounded-2xl shadow-2xl max-w-sm w-full p-6 space-y-4"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-5 h-5 text-destructive" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground">Please fix the following</h3>
                    <p className="text-sm text-muted-foreground">Before continuing, resolve these issues:</p>
                  </div>
                </div>
                <button onClick={() => setShowErrorModal(false)} className="p-1 rounded-lg hover:bg-muted transition-colors flex-shrink-0">
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
              <ul className="space-y-2">
                {(validationErrors ?? []).map((err, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-destructive flex-shrink-0" />
                    {err}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setShowErrorModal(false)}
                className="w-full px-4 py-2.5 rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors text-sm font-semibold shadow-sm"
              >
                Go back and fix
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Unsaved-draft reminder — soft, never blocks continuing */}
      <AnimatePresence>
        {showUnsavedModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowUnsavedModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ duration: 0.2 }}
              className="bg-card rounded-2xl shadow-2xl max-w-sm w-full p-6 space-y-4"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-warning/10 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-5 h-5 text-warning-text" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground">Don't forget to save</h3>
                    <p className="text-sm text-muted-foreground">{unsavedDraftWarning}</p>
                  </div>
                </div>
                <button onClick={() => setShowUnsavedModal(false)} className="p-1 rounded-lg hover:bg-muted transition-colors flex-shrink-0">
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => setShowUnsavedModal(false)}
                  className="w-full px-4 py-2.5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-semibold shadow-sm"
                >
                  Go back and save it
                </button>
                <button
                  onClick={() => { setShowUnsavedModal(false); onNext(); }}
                  className="w-full px-4 py-2 rounded-xl text-muted-foreground hover:bg-muted/60 transition-colors text-sm font-medium"
                >
                  Continue without saving
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
